import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { euro } from '../../lib/format';
import { useToasts } from '../../lib/toast';
import { useLang } from '../../lib/lang';
import type { OrderDto } from '@taj/types';

const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(40,20,10,.45)', display: 'flex',
  alignItems: 'center', justifyContent: 'center', zIndex: 70, padding: 16,
};
const sheet: React.CSSProperties = {
  background: '#fffdf8', borderRadius: 18, padding: 22, width: 'min(480px,95vw)',
  maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,.3)',
};
const itemsSummary = (o: OrderDto) => o.lines.map((l) => `${l.qty > 1 ? l.qty + '× ' : ''}${l.nameSnap}`).join(', ');

/** Rouvrir une commande fermée (terminée / annulée) — accessible depuis le back-office. */
export function ReopenModal({ onClose }: { onClose: () => void }) {
  const push = useToasts((s) => s.push);
  const lang = useLang((s) => s.lang);
  const t = (fr: string, en: string) => (lang === 'fr' ? fr : en);
  const [list, setList] = useState<OrderDto[] | null>(null);
  const [q, setQ] = useState('');
  useEffect(() => { api.closedOrders().then(setList).catch((e) => { push(e.message, 'warn'); setList([]); }); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const key = q.trim().toLowerCase();
  const shown = (list ?? []).filter((o) => !key || String(o.number).includes(key) || (o.clientName ?? '').toLowerCase().includes(key) || (o.token ?? '').toLowerCase().includes(key));

  const reopen = async (o: OrderDto) => {
    if (!confirm(t(`Rouvrir la commande ${o.token} ? Le manager sera notifié.`, `Reopen order ${o.token}? Manager will be notified.`))) return;
    try {
      await api.reopenOrder(o.id);
      push(`🔓 ${t('Commande rouverte', 'Order reopened')} · ${o.token}`);
      onClose();
    } catch (e: any) { push(e.message, 'warn'); }
  };

  const justif = async (o: OrderDto) => {
    push(t('🖨️ Impression du justificatif…', '🖨️ Printing receipt…'));
    try {
      const r = await api.printReceipt(o.id, 'Caisse');
      push(`✅ ${t('Justificatif imprimé', 'Receipt printed')} (${r.printer})`);
    } catch (e: any) { push(`❌ ${e.message}`, 'warn'); }
  };
  const emailIt = async (o: OrderDto) => {
    const email = prompt(t('Email du client :', 'Customer email:'));
    if (!email || !email.trim()) return;
    try { await api.emailReceipt(o.id, email.trim()); push(`📧 ${t('Justificatif envoyé à', 'Receipt sent to')} ${email.trim()}`); }
    catch (e: any) { push(e.message, 'warn'); }
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={sheet} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
          <b style={{ fontSize: '1.2rem', flex: 1 }}>🔓 {t('Rouvrir une commande fermée', 'Reopen a closed order')}</b>
          <button className="cs-btn" onClick={onClose}>✕</button>
        </div>
        <div className="cs-search" style={{ marginBottom: 12 }}>
          🔍 <input placeholder={t('n° commande, nom…', 'order n°, name…')} value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        {list === null ? (
          <div className="no-data">{t('Chargement…', 'Loading…')}</div>
        ) : shown.length === 0 ? (
          <div className="no-data">{t('Aucune commande fermée.', 'No closed order.')}</div>
        ) : (
          shown.map((o) => (
            <div key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 6px', borderBottom: '1px solid #eee' }}>
              <span style={{ fontWeight: 800, color: '#7a1f2b' }}>{o.token}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>{o.clientName ?? '—'} · {euro(o.totalCents)}</div>
                <div style={{ fontSize: '.78rem', color: '#999' }}>{o.status === 'CANCELLED' ? t('Annulée', 'Cancelled') : t('Terminée', 'Done')} · {itemsSummary(o)}</div>
              </div>
              <button className="cs-act" style={{ margin: 0, width: 'auto', padding: '8px 12px' }} onClick={() => justif(o)}>📄</button>
              <button className="cs-act" style={{ margin: 0, width: 'auto', padding: '8px 12px' }} onClick={() => emailIt(o)}>📧</button>
              <button className="cs-act" style={{ margin: 0, width: 'auto', padding: '8px 14px' }} onClick={() => reopen(o)}>🔓 {t('Rouvrir', 'Reopen')}</button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
